import { useCallback } from 'react';
import {
  IDataTable,
  IDataTableCreatePayload,
} from '@shared/models/data-table.model';
import { getActiveDataTables } from '@shared/helpers/data-tables';
import { useDataTableStore } from './data-table.store';
import { SemiPartial } from '@shared/models/type-helpers.model';
import { usePromiseSnackbar } from '@client/hooks/use-snackbar.hooks';

/**
 * All data tables for the current org, including soft-deleted rows still referenced by elements.
 */
export const useDataTables = () => useDataTableStore((state) => state.items);

export const getDataTables = () => useDataTableStore.getState().items;

/**
 * Active (non-deleted) data tables for admin list and connect-to-element UI.
 */
export const useActiveDataTables = () =>
  useDataTableStore((state) => getActiveDataTables(state.items));

export const getActiveDataTablesFromStore = () =>
  getActiveDataTables(useDataTableStore.getState().items);

export const useFetchDataTables = () =>
  useDataTableStore((state) => state.fetch);

export const useDataTableIsLoading = () =>
  useDataTableStore((state) => state.isLoading);

/**
 * Get a data table by ID
 * @param id The data table ID
 * @returns The data table or undefined if not found
 */
export const useDataTable = (id: string): IDataTable | undefined => {
  const items = useDataTables();
  return items?.find((item) => item.id === id);
};

/**
 * Get data table store state
 * @returns Data table store state
 */
export const useDataTableState = () => useDataTableStore();

/**
 * Update a data table and show a snackbar on success or error.
 * @returns A function to update a data table.
 */
export const useUpdateDataTable = () => {
  const updateDataTable = useDataTableStore((state) => state.update);
  const showSnackbar = usePromiseSnackbar();

  return useCallback(
    async (dataTable: SemiPartial<IDataTable, 'id'>): Promise<IDataTable> => {
      const { name } = dataTable;

      return showSnackbar(updateDataTable(dataTable), {
        successMessage: `Data table "${name}" updated successfully`,
        errorMessage: `Failed to update data table "${name}"`,
      });
    },
    [updateDataTable, showSnackbar],
  );
};

/**
 * Create a data table and show a snackbar on success or error.
 * @returns A function to create a data table.
 */
export const useCreateDataTable = () => {
  const createDataTable = useDataTableStore((state) => state.create);
  const showSnackbar = usePromiseSnackbar();

  return useCallback(
    async (dataTable: IDataTableCreatePayload): Promise<IDataTable> => {
      const { name } = dataTable;

      return showSnackbar(createDataTable(dataTable), {
        successMessage: `Data table "${name}" created successfully`,
        errorMessage: `Failed to create data table "${name}"`,
      });
    },
    [createDataTable, showSnackbar],
  );
};

/**
 * Delete a data table and show a snackbar on success or error.
 * @returns A function to delete a data table.
 */
export const useDeleteDataTable = () => {
  const deleteDataTable = useDataTableStore((state) => state.delete);
  const showSnackbar = usePromiseSnackbar();

  return useCallback(
    async (id: string, name: string): Promise<void> => {
      return showSnackbar(deleteDataTable(id), {
        successMessage: `Data table "${name}" deleted successfully`,
        errorMessage: `Failed to delete data table "${name}"`,
      });
    },
    [deleteDataTable, showSnackbar],
  );
};
